/**
 * EDIT A RESIDENT (WP-31 / MR-19).
 *
 * A resident's name, email and phone could not be changed anywhere in this
 * application. The Residents table had one row action and it was Remove, so the
 * only route to a corrected record was to end the tenancy and add the person
 * again — and, before WP-48, the re-add was then refused outright because their
 * email was still held by their own dormant row. A typed surname was a one-way
 * door.
 *
 * A MODAL DRIVEN BY STATE, NOT BY A ROUTE. The buildings list uses '?edit=<id>'
 * because a path segment would trip the dashboard's scroll reset and jump the
 * list to the top behind the modal (dashboard.tsx); the users list uses a path
 * because it has no scroll position worth keeping. This form is opened from a
 * row of a table that may be long and is often searched, so it keeps both the
 * scroll position and the search box's contents — neither of which survives a
 * navigation.
 *
 * THE FIELDS SENT ARE THE FIELDS THAT CHANGED. The server treats an absent key
 * as "leave this alone", so a request never carries a value the reader did not
 * touch — which is what stops a stale form from writing back over a change made
 * somewhere else between load and save.
 */
import React, { useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import toast from '../../../../utils/toast';
import { updatePropertyOrUserItem } from '../../../requests/requests';
import {
  palette,
  spacing,
  radius,
  shadow,
  layout,
  status,
} from '../../../../style-utils/theme';
import { FormModal } from '../../../modal/form-modal';
import { AppButton } from '../../../button/app-button';
import { InlineMessage } from '../../../../motion';
import { down } from '../../../../style-utils/media-queries';

export interface EditableResident {
  /** The OCCUPANCY id — the row that was clicked, which is what the server keys on. */
  id: number | string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  building_name?: string | null;
}

interface ResidentForm {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
}

interface DashboardEditResidentProps {
  resident: EditableResident;
  /** Passed through to FormModal so closing animates out. Undefined = open. */
  open?: boolean;
  onSaved: () => void;
  onClose: () => void;
}

export const DashboardEditResident: React.FC<DashboardEditResidentProps> = ({
  resident,
  open,
  onSaved,
  onClose,
}) => {
  const initial = useMemo<ResidentForm>(
    () => ({
      first_name: resident.first_name || '',
      last_name: resident.last_name || '',
      email: resident.email || '',
      phone_number: resident.phone_number || '',
    }),
    [resident]
  );
  const [form, setForm] = useState<ResidentForm>(initial);
  const [saving, setSaving] = useState(false);
  const saveRef = useRef(false);
  const [error, setError] = useState('');

  const change = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(current => ({ ...current, [e.target.name]: e.target.value }));
  };

  // Trimmed on BOTH sides of the comparison. Typing a trailing space and
  // deleting it again is not a change, and neither is a value the server would
  // have trimmed to what it already holds.
  const changedFields = (Object.keys(initial) as (keyof ResidentForm)[]).filter(
    key => form[key].trim() !== initial[key].trim()
  );
  const isDirty = changedFields.length > 0;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (saveRef.current) {
      return;
    }

    setError('');

    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError('First and last name are both required.');

      return;
    }
    if (!form.email.trim()) {
      setError('An email address is required — it is how they sign in.');

      return;
    }

    // NOTHING TO SAVE IS NOT AN ERROR, and it is not a save either. Answering
    // "saved" for a request that wrote nothing teaches the reader that the
    // button always works, which is the lesson that hides the time it did not.
    if (!isDirty) {
      toast.success('No changes to save');
      onClose();

      return;
    }

    saveRef.current = true;
    setSaving(true);
    try {
      const body: Partial<ResidentForm> = {};
      changedFields.forEach(key => {
        body[key] = form[key].trim();
      });

      await updatePropertyOrUserItem(`dashboard/tenants/${resident.id}`, body);

      // THE EMAIL IS THE LOGIN. Said out loud, because it is the one field on
      // this form whose change alters how the person gets into the product, and
      // an admin correcting a typo needs to know the old address stops working.
      toast.success(
        changedFields.includes('email')
          ? 'Resident updated. Their new email address is now how they sign in.'
          : 'Resident updated'
      );
      onSaved();
      onClose();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Could not update resident';
      setError(msg);
      toast.error(msg);
    } finally {
      saveRef.current = false;
      setSaving(false);
    }
  };

  const who = [initial.first_name, initial.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

  return (
    <FormModal
      open={open}
      isDirty={isDirty}
      discardMessage="These changes have not been saved. Close and lose them?"
      title="Edit resident"
      subtitle={
        resident.building_name
          ? `${who || 'This resident'} · ${resident.building_name}`
          : who || undefined
      }
      onClose={onClose}
      footer={
        <>
          <Cancel variant="secondary" onClick={onClose}>
            Cancel
          </Cancel>
          <Save
            type="submit"
            form="edit-resident-form"
            loading={saving}
            loadingLabel="Saving"
          >
            Save changes
          </Save>
        </>
      }
    >
      <Form id="edit-resident-form" onSubmit={submit}>
        <Row>
          <Field>
            <Label htmlFor="resident-first-name">First name</Label>
            <Input
              id="resident-first-name"
              name="first_name"
              value={form.first_name}
              onChange={change}
              maxLength={128}
              required
            />
          </Field>
          <Field>
            <Label htmlFor="resident-last-name">Last name</Label>
            <Input
              id="resident-last-name"
              name="last_name"
              value={form.last_name}
              onChange={change}
              maxLength={128}
              required
            />
          </Field>
        </Row>
        <Field>
          <Label htmlFor="resident-email">Email (login)</Label>
          <Input
            id="resident-email"
            name="email"
            type="email"
            value={form.email}
            onChange={change}
            autoComplete="email"
            required
          />
          <Hint>
            This is the address they sign in with. Changing it replaces the old
            one immediately.
          </Hint>
        </Field>
        <Field>
          <Label htmlFor="resident-phone">Phone</Label>
          <Input
            id="resident-phone"
            name="phone_number"
            type="tel"
            value={form.phone_number}
            onChange={change}
            autoComplete="tel"
            placeholder="Optional"
          />
        </Field>

        {/* Unfolds under the fields rather than popping in. */}
        <InlineMessage
          role="alert"
          message={error ? <ErrorText>{error}</ErrorText> : undefined}
        />
      </Form>
    </FormModal>
  );
};

const Form = styled.form`
  max-width: 560px;
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.md};

  /* down('sm'), matching the add-resident form and onboarding's FieldRow, so a
     form built from the same parts breaks to one column at the same width. */
  ${down('sm')} {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${palette.slate};
`;

const Hint = styled.span`
  color: ${palette.slate};
  font-size: 12px;
`;

const Input = styled.input`
  height: ${layout.fieldHeight};
  padding: 0 ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  font: inherit;
  color: ${palette.ink};
  background: ${palette.white};
  outline: none;

  &:focus {
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

const ErrorText = styled.div`
  color: ${status.alert.fg};
  font-size: 13px;
`;

const Save = styled(AppButton)`
  min-width: 120px;
`;

const Cancel = styled(AppButton)``;
